import { z } from 'zod';

const httpsUrl = z.string().url().refine((value) => new URL(value).protocol === 'https:', 'HTTPS required');
const buttonSchema = z.discriminatedUnion('type', [
  z.object({ id: z.string().min(1).max(48), type: z.literal('whatsapp'), label: z.string().min(1).max(80), enabled: z.boolean(), order: z.number().int() }),
  z.object({ id: z.string().min(1).max(48), type: z.literal('email'), label: z.string().min(1).max(80), enabled: z.boolean(), order: z.number().int() }),
  z.object({ id: z.string().min(1).max(48), type: z.literal('tracking'), label: z.string().min(1).max(80), enabled: z.boolean(), order: z.number().int() }),
  z.object({ id: z.string().min(1).max(48), type: z.literal('custom'), label: z.string().min(1).max(80), enabled: z.boolean(), order: z.number().int(), url: httpsUrl })
]);

export const appConfigSchema = z.object({
  version: z.literal(1),
  title: z.string().min(1).max(100),
  description: z.string().max(300),
  timezone: z.string().min(1).max(64),
  logo: z.object({ visible: z.boolean(), asset: z.enum(['transparent', 'white', 'original']) }),
  theme: z.object({ primary: z.string().regex(/^#[0-9a-fA-F]{6}$/), action: z.string().regex(/^#[0-9a-fA-F]{6}$/), accent: z.string().regex(/^#[0-9a-fA-F]{6}$/) }),
  whatsapp: z.object({ number: z.string().max(24), message: z.string().max(500) }),
  email: z.object({ address: z.union([z.literal(''), z.string().email()]), subject: z.string().max(160) }),
  messages: z.object({ notFound: z.string().max(300), unavailable: z.string().max(300) }),
  buttons: z.array(buttonSchema).max(20)
});

export type AppConfig = z.infer<typeof appConfigSchema>;

export function buildDefaultConfig(input: { email?: string; whatsappNumber?: string } = {}): AppConfig {
  return {
    version: 1,
    title: 'How can we help?',
    description: 'Choose an option below. We’re happy to help.',
    timezone: 'Asia/Shanghai',
    logo: { visible: true, asset: 'white' },
    theme: { primary: '#69afe6', action: '#276f9f', accent: '#f4cf69' },
    whatsapp: { number: input.whatsappNumber ?? '', message: 'Hello Tuftlet, I need help with my order.' },
    email: { address: input.email ?? 'test@example.com', subject: 'Tuftlet customer support' },
    messages: {
      notFound: "We couldn’t find a matching order. Please check your information or contact us on WhatsApp.",
      unavailable: 'Order tracking is temporarily unavailable. Please try again later or contact us on WhatsApp.'
    },
    buttons: [
      { id: 'whatsapp', type: 'whatsapp', label: 'Chat on WhatsApp', enabled: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Us', enabled: true, order: 2 },
      { id: 'tracking', type: 'tracking', label: 'Track My Order', enabled: true, order: 3 }
    ]
  };
}

export function validateConfig(value: unknown): AppConfig {
  return appConfigSchema.parse(value);
}

export function getButtonTarget(config: AppConfig, id: string): string | null {
  const button = config.buttons.find((candidate) => candidate.id === id && candidate.enabled);
  if (!button) return null;
  if (button.type === 'whatsapp') {
    const number = config.whatsapp.number.replace(/\D/g, '');
    if (!number) return null;
    return `https://wa.me/${number}?text=${encodeURIComponent(config.whatsapp.message)}`;
  }
  if (button.type === 'custom') return button.url;
  return null;
}

export const logoAssets: Record<AppConfig['logo']['asset'], string> = {
  transparent: '/assets/tuftlet-logo-transparent.png',
  white: '/assets/tuftlet-logo-white-bg.png',
  original: '/assets/tuftlet-logo-original.png'
};

export const trackingLinkTemplates = {
  seventeenTrack: 'https://www.17track.net/en?nums={trackingNumber}'
} as const;

export function build17TrackUrl(trackingNumber: string): string {
  return trackingLinkTemplates.seventeenTrack.replace('{trackingNumber}', encodeURIComponent(trackingNumber));
}
